/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / SETTINGS-CAMPAIGNS — Kampagnen-Verwaltung (S227 CUT D)
   =============================================================================
   Eigener Bereich neben dem Builder (Settings → Mail): Liste + Editor-Modal
   (Builder-Muster) + zweistufige Freigabe (Editor → Empfänger-Vorschau →
   finaler Accept sofort/geplant). Empfänger = ausschließlich confirmed
   Abonnenten (Server), Audience wird ERST beim Versand final ausgewertet.

   create-on-accept-only: „Abbrechen" ohne Speichern/Freigabe/Testmail
   hinterlässt NIE eine Zeile. Keine nativen Dialoge (styledConfirm für Storno).
   Non-destruktive Renders. Anhang-Upload erst beim Speichern (kein order_receipt).
============================================================================ */

import { htmlEscape, showToast, getRestaurantTimezone } from '../../utils/helpers.js';
import { createDarkOverlay } from '../ui/overlay.js';
import { wrapBodyWithBrandHtml } from '../../core/branding.js';
import { fillRuleTemplate } from './settings-mail-builder.js';
import {
  createCampaign, updateCampaign, deleteCampaign, getCampaign,
  getCampaignsCache, isCampaignsLoaded, previewAudience,
  startCampaignNow, scheduleCampaign, cancelCampaign, testSendCampaign,
} from '../../core/campaigns-data.js';

const OVERLAY_ID = 'campaign-editor-overlay';
const STATIC_MAX_BYTES = 1500000;
const TOKENS = ['{{name}}', '{{brand}}'];

let _draft = null;
let _editId = null;      // persistierte Kampagnen-ID (null = noch nicht gespeichert)
let _pendingFile = null;
let _lastField = 'body';
let _mode = 'edit';      // 'edit' | 'release'

function _state() {
  return (window.App && window.App.storage && window.App.storage._getInternalState())
    || { settings: {}, menu: [] };
}
function supa() { return (window.App && window.App.supabase) || null; }

// -----------------------------------------------------------------------------
// Pure Helpers (Vitest)
// -----------------------------------------------------------------------------

/** Baut den audience_filter-jsonb aus den Formularwerten. Leere Felder fallen raus. */
export function buildAudienceFilter(form) {
  const f = {};
  const minO = parseInt(form.minOrders, 10);
  if (Number.isFinite(minO) && minO > 0) f.min_orders = minO;
  const months = parseInt(form.activityMonths, 10);
  if (form.activityMode === 'active' && Number.isFinite(months) && months > 0) f.active_within_months = months;
  if (form.activityMode === 'inactive' && Number.isFinite(months) && months > 0) f.inactive_since_months = months;
  const tags = String(form.tags || '').split(',').map(t => t.trim()).filter(Boolean);
  if (tags.length) f.tags = tags;
  return f;
}

/** Validiert einen Kampagnen-Draft. → { ok, error }. */
export function validateCampaign(d) {
  if (!d || !String(d.name || '').trim()) return { ok: false, error: 'Bitte gib der Kampagne einen Namen.' };
  if (!String(d.subject || '').trim()) return { ok: false, error: 'Bitte gib einen Betreff ein.' };
  if (!String(d.body || '').trim()) return { ok: false, error: 'Bitte gib einen Text ein.' };
  return { ok: true, error: null };
}

/** Status → Anzeige-Chip-Text. */
export function campaignStatusLabel(status) {
  switch (status) {
    case 'draft':     return 'Entwurf';
    case 'scheduled': return 'Geplant';
    case 'sending':   return 'Wird gesendet';
    case 'done':      return 'Gesendet';
    case 'cancelled': return 'Storniert';
    default:          return status || '—';
  }
}

// -----------------------------------------------------------------------------
// Liste (in der Vorlagen-Übersicht)
// -----------------------------------------------------------------------------

// S227-HF2 (#4): scheduled_at ist ein timestamptz-ISO (UTC). In der Restaurant-
// Timezone rendern (getRestaurantTimezone = TZ-Single-Source), NICHT die rohen
// UTC-Ziffern (zeigte 18:15 statt 20:15).
function _fmtWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', timeZone: getRestaurantTimezone(),
    }) + ' Uhr';
  } catch (_) { return String(iso); }
}

/** Kampagnen-Zeilen + „+ Neue Kampagne"-Button (für die Übersicht). */
export function renderCampaignsRows() {
  const addBtn = `
    <div style="padding:12px 16px;">
      <button type="button" class="btn btn-secondary btn-sm" onclick="openCampaignEditor()">+ Neue Kampagne</button>
    </div>`;
  if (!isCampaignsLoaded()) return `<p style="padding:16px;color:var(--ink-soft);">Kampagnen werden geladen …</p>` + addBtn;
  const rows = getCampaignsCache();
  if (!rows.length) return `<p style="padding:16px;color:var(--ink-soft);">Noch keine Kampagnen angelegt.</p>` + addBtn;
  return rows.map(c => _campaignRow(c)).join('') + addBtn;
}

function _campaignRow(c) {
  const editable = c.status === 'draft' || c.status === 'scheduled';
  const cancelBtn = c.status === 'scheduled'
    ? `<button type="button" class="btn btn-ghost btn-sm" onclick="cancelCampaignRow('${htmlEscape(c.id)}')">Storno</button>` : '';
  const whenTxt = c.status === 'scheduled' && c.scheduled_at ? ` · geplant für ${_fmtWhen(c.scheduled_at)}` : '';
  return `
    <div class="mail-kind-row" data-campaign-row="${htmlEscape(c.id)}">
      <div class="mail-kind-row-main">
        <button type="button" class="mail-kind-open" onclick="openCampaignEditor('${htmlEscape(c.id)}')" ${editable ? '' : 'title="Nur Entwürfe/geplante Kampagnen sind bearbeitbar"'}>
          <span class="label">${htmlEscape(c.name || '(ohne Namen)')} <span class="badge-soft" style="font-size:.66rem;">${htmlEscape(campaignStatusLabel(c.status))}</span></span>
          <span class="desc">${c.recipient_count || 0} Empfänger · ${c.sent_count || 0} gesendet${whenTxt}</span>
        </button>
        <div class="mail-kind-ctl">${cancelBtn}</div>
      </div>
    </div>`;
}

// -----------------------------------------------------------------------------
// Editor-Modal
// -----------------------------------------------------------------------------

function _blankDraft() {
  return { name: '', subject: '', body: '', attachment: null,
    audience: { minOrders: '', activityMode: 'any', activityMonths: '', tags: '' } };
}

export function openCampaignEditor(id) {
  closeCampaignEditor();
  _editId = id || null;
  _pendingFile = null;
  _lastField = 'body';
  _mode = 'edit';
  if (_editId) {
    const c = getCampaign(_editId);
    if (!c) { showToast('Kampagne nicht gefunden.', 'error'); return; }
    if (c.status !== 'draft' && c.status !== 'scheduled') { showToast('Diese Kampagne ist nicht mehr bearbeitbar.', 'error'); return; }
    const af = c.audience_filter || {};
    _draft = {
      name: c.name, subject: c.subject, body: c.body, attachment: c.attachment || null,
      audience: {
        minOrders: af.min_orders != null ? String(af.min_orders) : '',
        activityMode: af.active_within_months ? 'active' : (af.inactive_since_months ? 'inactive' : 'any'),
        activityMonths: String(af.active_within_months || af.inactive_since_months || ''),
        tags: Array.isArray(af.tags) ? af.tags.join(', ') : '',
      },
    };
  } else {
    _draft = _blankDraft();
  }
  const overlay = createDarkOverlay({ id: OVERLAY_ID, zIndex: 9000, onClose: closeCampaignEditor });
  overlay.innerHTML = `
    <div id="campaign-editor-panel" role="dialog" aria-modal="true"
         style="background:var(--bg-elev,#15201b);color:var(--ink,#e8efe8);width:min(780px,100%);max-height:92vh;
                display:flex;flex-direction:column;border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,.5);overflow:hidden;"></div>`;
  document.body.appendChild(overlay);
  _paint();
}

export function closeCampaignEditor() {
  const el = document.getElementById(OVERLAY_ID);
  if (el) {
    if (typeof el._ggDetachKey === 'function') el._ggDetachKey();
    if (el.parentNode) el.parentNode.removeChild(el);
  }
  _draft = null; _editId = null; _pendingFile = null; _mode = 'edit';
}

function _syncDraftFromDom() {
  if (!_draft) return;
  const g = (id) => document.getElementById(id);
  if (g('cmp-name')) _draft.name = g('cmp-name').value;
  if (g('cmp-subject')) _draft.subject = g('cmp-subject').value;
  if (g('cmp-body')) _draft.body = g('cmp-body').value;
  if (g('cmp-min-orders')) _draft.audience.minOrders = g('cmp-min-orders').value;
  if (g('cmp-activity-mode')) _draft.audience.activityMode = g('cmp-activity-mode').value;
  if (g('cmp-activity-months')) _draft.audience.activityMonths = g('cmp-activity-months').value;
  if (g('cmp-tags')) _draft.audience.tags = g('cmp-tags').value;
  if (g('cmp-attachment')) {
    const v = g('cmp-attachment').value;
    _draft.attachment = v === 'static'
      ? (_draft.attachment && _draft.attachment.type === 'static' ? _draft.attachment : { type: 'static' })
      : null;
  }
}

function _paint() {
  const panel = document.getElementById('campaign-editor-panel');
  if (!panel || !_draft) return;
  panel.innerHTML = _mode === 'release' ? _renderRelease() : _renderEditor();
  if (_mode === 'edit') _mountPreview();
}

function _renderEditor() {
  const d = _draft;
  const a = d.audience;
  const chips = TOKENS.map(t =>
    `<button type="button" onclick="campaignInsertToken('${htmlEscape(t)}')"
       style="margin:0 6px 6px 0;cursor:pointer;border:1px solid var(--border,#2a352e);background:rgba(255,255,255,.06);color:var(--ink,#e8efe8);font-size:.8rem;padding:3px 9px;border-radius:6px;font-family:ui-monospace,Menlo,monospace;">${htmlEscape(t)}</button>`
  ).join('');
  const attType = d.attachment && d.attachment.type;
  const staticInfo = (attType === 'static' && d.attachment && d.attachment.filename)
    ? `<div style="color:var(--ink-muted,#8b9890);font-size:.78rem;margin-top:4px;">Datei: ${htmlEscape(d.attachment.filename)}</div>` : '';
  return `
    <div style="padding:16px 20px;border-bottom:1px solid var(--border,#2a352e);display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <strong style="font-size:1.05rem;">${_editId ? 'Kampagne bearbeiten' : 'Neue Kampagne'}</strong>
      <button onclick="closeCampaignEditor()" aria-label="Schließen" style="cursor:pointer;border:0;background:transparent;color:var(--ink-soft,#b3c0b6);font-size:1.15rem;padding:4px 8px;">✕</button>
    </div>
    <div style="flex:1;min-height:0;overflow-y:auto;padding:18px 20px;background:var(--bg-elev,#15201b);">
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Name (intern)</label>
        <input type="text" id="cmp-name" value="${htmlEscape(d.name)}" placeholder="z. B. Sommer-Aktion Juli" style="width:100%;">
      </div>
      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Betreff</label>
        <input type="text" id="cmp-subject" value="${htmlEscape(d.subject)}" onfocus="campaignTrackField('subject')" style="width:100%;">
      </div>
      <div class="field" style="margin-bottom:8px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Text</label>
        <textarea id="cmp-body" rows="9" onfocus="campaignTrackField('body')" style="width:100%;font-family:ui-monospace,Menlo,monospace;font-size:.88rem;">${htmlEscape(d.body)}</textarea>
      </div>
      <div style="margin:0 0 14px;">
        <div style="color:var(--ink-muted,#8b9890);font-size:.78rem;margin-bottom:6px;">Platzhalter:</div>${chips}
      </div>

      <div style="border-top:1px solid var(--border,#2a352e);padding-top:12px;margin-bottom:12px;">
        <div style="color:var(--ink-soft,#b3c0b6);font-size:.85rem;margin-bottom:8px;">Empfänger-Segment (optional, alle Filter UND-verknüpft)</div>
        <div class="field" style="margin-bottom:10px;">
          <label style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;">Mindestanzahl Bestellungen</label>
          <input type="number" id="cmp-min-orders" min="0" value="${htmlEscape(a.minOrders)}" placeholder="egal" style="width:100%;">
        </div>
        <div class="field" style="margin-bottom:10px;display:flex;gap:8px;align-items:end;">
          <div style="flex:1;">
            <label style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;">Aktivität</label>
            <select id="cmp-activity-mode" style="width:100%;">
              <option value="any" ${a.activityMode === 'any' ? 'selected' : ''}>egal</option>
              <option value="active" ${a.activityMode === 'active' ? 'selected' : ''}>aktiv in den letzten … Monaten</option>
              <option value="inactive" ${a.activityMode === 'inactive' ? 'selected' : ''}>inaktiv seit … Monaten</option>
            </select>
          </div>
          <div style="width:110px;">
            <input type="number" id="cmp-activity-months" min="1" value="${htmlEscape(a.activityMonths)}" placeholder="Monate" style="width:100%;">
          </div>
        </div>
        <div class="field" style="margin-bottom:4px;">
          <label style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;">Tags (kommagetrennt)</label>
          <input type="text" id="cmp-tags" value="${htmlEscape(a.tags)}" placeholder="z. B. stammgast, vip" style="width:100%;">
        </div>
        <div style="color:var(--ink-muted,#8b9890);font-size:.72rem;">Sobald ein Filter aktiv ist, werden Abonnenten ohne Gästeprofil ausgeschlossen.</div>
      </div>

      <div class="field" style="margin-bottom:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Anhang</label>
        <select id="cmp-attachment" onchange="campaignOnAttachmentChange()" style="width:100%;">
          <option value="none" ${!attType ? 'selected' : ''}>Kein Anhang</option>
          <option value="static" ${attType === 'static' ? 'selected' : ''}>Datei-Upload (fest)</option>
        </select>
        <div id="cmp-static-field" style="margin-top:8px;${attType === 'static' ? '' : 'display:none;'}">
          <input type="file" id="cmp-static-file" onchange="campaignOnFilePicked(event)" style="width:100%;">
          ${staticInfo}
          <div style="color:var(--ink-muted,#8b9890);font-size:.72rem;margin-top:4px;">max. 1,5 MB</div>
        </div>
      </div>

      <div style="border-top:1px solid var(--border,#2a352e);padding-top:12px;">
        <div style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;margin-bottom:8px;">Vorschau (inkl. gesetzlich vorgeschriebenem Abmelde-Footer):</div>
        <div id="cmp-preview-host" style="width:100%;height:36vh;background:#efe9dd;border-radius:8px;overflow:hidden;"></div>
      </div>
    </div>
    <div style="padding:14px 20px;border-top:1px solid var(--border,#2a352e);display:flex;gap:10px;justify-content:space-between;align-items:center;flex-wrap:wrap;">
      <div>${_editId ? `<button type="button" class="btn btn-danger btn-sm" onclick="campaignDelete()">Löschen</button>` : ''}</div>
      <div style="display:flex;gap:10px;flex-wrap:wrap;">
        <button type="button" class="btn btn-ghost" onclick="closeCampaignEditor()">Abbrechen</button>
        <button type="button" class="btn btn-secondary btn-sm" onclick="campaignSaveDraft()">Als Entwurf speichern</button>
        <button type="button" class="btn btn-primary" onclick="campaignGoRelease()">Empfänger &amp; Freigabe →</button>
      </div>
    </div>`;
}

function _renderRelease() {
  return `
    <div style="padding:16px 20px;border-bottom:1px solid var(--border,#2a352e);display:flex;justify-content:space-between;align-items:center;gap:12px;">
      <strong style="font-size:1.05rem;">Freigabe — ${htmlEscape(_draft.name)}</strong>
      <button onclick="closeCampaignEditor()" aria-label="Schließen" style="cursor:pointer;border:0;background:transparent;color:var(--ink-soft,#b3c0b6);font-size:1.15rem;padding:4px 8px;">✕</button>
    </div>
    <div style="flex:1;min-height:0;overflow-y:auto;padding:18px 20px;background:var(--bg-elev,#15201b);">
      <div id="cmp-audience" style="margin-bottom:16px;color:var(--ink-soft,#b3c0b6);">Empfänger werden berechnet …</div>

      <div class="field" style="margin-bottom:16px;border-top:1px solid var(--border,#2a352e);padding-top:12px;">
        <label style="color:var(--ink-soft,#b3c0b6);">Testmail senden an</label>
        <div style="display:flex;gap:8px;">
          <input type="email" id="cmp-test-email" value="${htmlEscape(_defaultTestEmail())}" style="flex:1;">
          <button type="button" class="btn btn-secondary btn-sm" onclick="campaignTestSend()">Testmail</button>
        </div>
        <div style="color:var(--ink-muted,#8b9890);font-size:.72rem;margin-top:4px;">Zeigt das Original inkl. Footer — ohne Zähler-Effekt.</div>
      </div>

      <div style="border-top:1px solid var(--border,#2a352e);padding-top:12px;">
        <div style="color:var(--ink-soft,#b3c0b6);font-size:.85rem;margin-bottom:8px;">Versand</div>
        <div class="field" style="margin-bottom:10px;">
          <label style="color:var(--ink-soft,#b3c0b6);font-size:.82rem;">Geplanter Zeitpunkt (leer = sofort)</label>
          <input type="datetime-local" id="cmp-schedule-at" style="width:100%;">
        </div>
      </div>
    </div>
    <div style="padding:14px 20px;border-top:1px solid var(--border,#2a352e);display:flex;gap:10px;justify-content:space-between;align-items:center;flex-wrap:wrap;">
      <button type="button" class="btn btn-ghost" onclick="campaignBackToEdit()">← Bearbeiten</button>
      <div style="display:flex;gap:10px;">
        <button type="button" class="btn btn-secondary btn-sm" onclick="campaignSchedule()">Planen</button>
        <button type="button" class="btn btn-primary" onclick="campaignSendNow()">Jetzt senden</button>
      </div>
    </div>`;
}

function _defaultTestEmail() {
  const s = _state().settings || {};
  return s.restaurantEmail || (window.App && window.App.auth && typeof window.App.auth.getEmail === 'function' && window.App.auth.getEmail()) || '';
}

function _mountPreview() {
  const host = document.getElementById('cmp-preview-host');
  if (!host || !_draft) return;
  const bodyFilled = fillRuleTemplate(_draft.body, { name: 'Gast', brand: (_state().settings && _state().settings.restaurantName) || 'unser Restaurant' });
  const restaurantName = (_state().settings && _state().settings.restaurantName) || '';
  host.innerHTML = '';
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', '');
  frame.setAttribute('title', 'Kampagnen-Vorschau');
  frame.style.cssText = 'width:100%;height:100%;border:0;background:#efe9dd;';
  host.appendChild(frame);
  Promise.resolve()
    .then(() => wrapBodyWithBrandHtml(bodyFilled, { restaurantName, unsubscribeLink: '#abmelden' }))
    .then((html) => { if (frame.isConnected) frame.srcdoc = html; })
    .catch(() => { if (frame.isConnected) frame.srcdoc = '<p style="font-family:sans-serif;padding:16px;">Vorschau nicht verfügbar.</p>'; });
}

// ---- Inline-Handler ---------------------------------------------------------

export function campaignTrackField(f) { if (f === 'subject' || f === 'body') _lastField = f; }
export function campaignInsertToken(token) {
  const id = _lastField === 'subject' ? 'cmp-subject' : 'cmp-body';
  const el = document.getElementById(id);
  if (!el) return;
  const s = el.selectionStart != null ? el.selectionStart : el.value.length;
  const e = el.selectionEnd != null ? el.selectionEnd : el.value.length;
  el.value = el.value.slice(0, s) + token + el.value.slice(e);
  const caret = s + token.length;
  el.focus();
  try { el.setSelectionRange(caret, caret); } catch (_) { /* ignore */ }
  _syncDraftFromDom();
}
export function campaignOnAttachmentChange() { _syncDraftFromDom(); _paint(); }
export function campaignOnFilePicked(ev) {
  const file = ev && ev.target && ev.target.files && ev.target.files[0];
  if (!file) return;
  if (file.size > STATIC_MAX_BYTES) { showToast('Die Datei ist größer als 1,5 MB.', 'error'); ev.target.value = ''; return; }
  _pendingFile = file;
  _draft.attachment = { type: 'static', filename: file.name, byte_size: file.size, content_type: file.type || 'application/octet-stream' };
}

async function _uploadPendingFileIfAny() {
  if (!_pendingFile) return;
  const client = supa();
  if (!client) throw new Error('Kein Supabase-Client für den Upload.');
  const rid = (window.App && typeof window.App.getCurrentRestaurantId === 'function' && window.App.getCurrentRestaurantId()) || 'rest';
  const safe = String(_pendingFile.name || 'anhang').replace(/[^\w.\-]+/g, '_');
  const path = `${rid}/campaign_${Date.now()}_${safe}`;
  const { error } = await client.storage.from('mail-attachments').upload(path, _pendingFile, {
    contentType: _pendingFile.type || 'application/octet-stream', upsert: false,
  });
  if (error) throw error;
  _draft.attachment = { type: 'static', path, filename: _pendingFile.name, byte_size: _pendingFile.size, content_type: _pendingFile.type || 'application/octet-stream' };
  _pendingFile = null;
}

/** Persistiert den Draft (create/update) inkl. Upload. Liefert die ID. */
async function _persistDraft() {
  _syncDraftFromDom();
  const v = validateCampaign(_draft);
  if (!v.ok) { showToast(v.error, 'error'); return null; }
  if (_draft.attachment && _draft.attachment.type === 'static' && !_pendingFile && !_draft.attachment.path) {
    showToast('Bitte eine Datei für den Anhang auswählen.', 'error'); return null;
  }
  await _uploadPendingFileIfAny();
  const payload = {
    name: _draft.name, subject: _draft.subject, body: _draft.body,
    attachment: _draft.attachment || null, audience_filter: buildAudienceFilter(_draft.audience),
  };
  if (_editId) { await updateCampaign(_editId, payload); }
  else { const row = await createCampaign(payload); _editId = row.id; }
  return _editId;
}

export async function campaignSaveDraft() {
  try {
    const id = await _persistDraft();
    if (!id) return;
    showToast('Entwurf gespeichert.', 'success');
    closeCampaignEditor();
    if (typeof window.renderSettings === 'function') window.renderSettings();
  } catch (e) { console.error('[campaignSaveDraft]', e); showToast('Speichern fehlgeschlagen: ' + (e && e.message ? e.message : e), 'error'); }
}

export async function campaignGoRelease() {
  try {
    const id = await _persistDraft();
    if (!id) return;
    _mode = 'release';
    _paint();
    _loadAudiencePreview();
  } catch (e) { console.error('[campaignGoRelease]', e); showToast('Speichern fehlgeschlagen: ' + (e && e.message ? e.message : e), 'error'); }
}

export function campaignBackToEdit() { _mode = 'edit'; _paint(); }

async function _loadAudiencePreview() {
  const box = document.getElementById('cmp-audience');
  if (!box) return;
  try {
    const filter = buildAudienceFilter(_draft.audience);
    const res = await previewAudience(filter);
    const sample = Array.isArray(res.sample) ? res.sample : [];
    const unlinked = Number(res.unlinked_excluded) || 0;
    box.innerHTML = `
      <div style="font-size:1.4rem;font-weight:700;color:var(--ink,#e8efe8);">${Number(res.count) || 0} Empfänger</div>
      <div style="color:var(--ink-soft,#b3c0b6);font-size:.85rem;margin-top:4px;">bestätigte Abonnenten, die den Filtern entsprechen (final beim Versand)</div>
      ${sample.length ? `<div style="color:var(--ink-muted,#8b9890);font-size:.8rem;margin-top:8px;">z. B. ${sample.map(htmlEscape).join(' · ')}</div>` : ''}
      ${unlinked > 0 ? `<div style="color:#e7a82d;font-size:.8rem;margin-top:8px;">⚠️ ${unlinked} Abonnent${unlinked === 1 ? '' : 'en'} ohne Gästeprofil ${unlinked === 1 ? 'wird' : 'werden'} bei aktiven Filtern ausgeschlossen.</div>` : ''}`;
  } catch (e) {
    console.error('[audience-preview]', e);
    box.innerHTML = `<div style="color:var(--danger,#c8102e);">Empfänger konnten nicht berechnet werden.</div>`;
  }
}

export async function campaignTestSend() {
  if (!_editId) { showToast('Bitte zuerst speichern.', 'error'); return; }
  const el = document.getElementById('cmp-test-email');
  const email = el ? String(el.value || '').trim() : '';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { showToast('Bitte eine gültige Test-Adresse eingeben.', 'error'); return; }
  try {
    const res = await testSendCampaign(_editId, email);
    if (res && res.ok) showToast('Testmail wurde in die Warteschlange gestellt.', 'success');
    else showToast('Testmail nicht möglich.', 'error');
  } catch (e) { console.error('[campaignTestSend]', e); showToast('Testmail fehlgeschlagen.', 'error'); }
}

export async function campaignSendNow() {
  if (!_editId) return;
  const doSend = async () => {
    try {
      const res = await startCampaignNow(_editId);
      if (res && res.ok) showToast(`Kampagne gestartet — ${res.recipient_count || 0} Empfänger.`, 'success');
      else showToast('Versand nicht möglich (Status?).', 'error');
      closeCampaignEditor();
      if (typeof window.renderSettings === 'function') window.renderSettings();
    } catch (e) { console.error('[campaignSendNow]', e); showToast('Versand fehlgeschlagen.', 'error'); }
  };
  if (typeof window.styledConfirm === 'function') {
    const ok = await window.styledConfirm('Kampagne jetzt an alle passenden Abonnenten senden?', { confirmText: 'Jetzt senden' });
    if (ok) doSend();
  } else { doSend(); }
}

export async function campaignSchedule() {
  if (!_editId) return;
  const el = document.getElementById('cmp-schedule-at');
  const val = el ? el.value : '';
  if (!val) { showToast('Bitte einen Zeitpunkt wählen (oder „Jetzt senden“).', 'error'); return; }
  const iso = new Date(val).toISOString();
  try {
    await scheduleCampaign(_editId, iso);
    showToast('Kampagne geplant.', 'success');
    closeCampaignEditor();
    if (typeof window.renderSettings === 'function') window.renderSettings();
  } catch (e) { console.error('[campaignSchedule]', e); showToast('Planen fehlgeschlagen.', 'error'); }
}

export async function campaignDelete() {
  if (!_editId) return;
  const doDelete = async () => {
    try {
      await deleteCampaign(_editId);
      showToast('Kampagne gelöscht.', 'success');
      closeCampaignEditor();
      if (typeof window.renderSettings === 'function') window.renderSettings();
    } catch (e) { console.error('[campaignDelete]', e); showToast('Löschen fehlgeschlagen.', 'error'); }
  };
  if (typeof window.styledConfirm === 'function') {
    const ok = await window.styledConfirm('Diese Kampagne wirklich löschen?', { danger: true });
    if (ok) doDelete();
  } else { doDelete(); }
}

export async function cancelCampaignRow(id) {
  const doCancel = async () => {
    try {
      const ok = await cancelCampaign(id);
      showToast(ok ? 'Kampagne storniert.' : 'Nicht mehr stornierbar (Versand läuft schon).', ok ? 'success' : 'error');
      if (typeof window.renderSettings === 'function') window.renderSettings();
    } catch (e) { console.error('[cancelCampaignRow]', e); showToast('Storno fehlgeschlagen.', 'error'); }
  };
  if (typeof window.styledConfirm === 'function') {
    const ok = await window.styledConfirm('Geplante Kampagne stornieren?', { danger: true });
    if (ok) doCancel();
  } else { doCancel(); }
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    openCampaignEditor, closeCampaignEditor,
    campaignTrackField, campaignInsertToken, campaignOnAttachmentChange, campaignOnFilePicked,
    campaignSaveDraft, campaignGoRelease, campaignBackToEdit,
    campaignTestSend, campaignSendNow, campaignSchedule, campaignDelete, cancelCampaignRow,
    renderCampaignsRows,
  });
}
